<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CockpitFinancials, FinancialBucket } from '@/types/cockpit'

const StackedFeeBarChart = defineAsyncComponent(
  () => import('./StackedFeeBarChart.vue'),
)

const props = defineProps<{
  data: CockpitFinancials | null
  loading: boolean
  error: string | null
  bucket: FinancialBucket
}>()

const emit = defineEmits<{
  'update:bucket': [value: FinancialBucket]
  retry: []
}>()

const { t, locale } = useI18n()

// The selected currency index (shown when multiple currencies are present).
const selectedCurrencyIndex = ref(0)

const currentSeries = computed(() => {
  if (!props.data || props.data.currencies.length === 0) return null
  return props.data.currencies[selectedCurrencyIndex.value] ?? props.data.currencies[0]
})

const showCurrencySelector = computed(
  () => props.data !== null && props.data.currencies.length > 1,
)

function formatMoney(value: string, currency: string): string {
  const num = parseFloat(value)
  return new Intl.NumberFormat(locale.value, { style: 'currency', currency: currency.toUpperCase() }).format(num)
}

const summaryLine = computed(() => {
  if (!currentSeries.value) return null
  const { summary, currency } = currentSeries.value
  return {
    grossTake: formatMoney(summary.gross_take, currency),
    stripeCost: formatMoney(summary.stripe_cost, currency),
    net: formatMoney(summary.net, currency),
  }
})

const hasData = computed(
  () => currentSeries.value !== null && currentSeries.value.buckets.length > 0,
)

const buckets = computed(() => currentSeries.value?.buckets ?? [])
const currency = computed(() => currentSeries.value?.currency ?? 'eur')
</script>

<template>
  <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6">
    <!-- Section header -->
    <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
      <h2 class="text-lg font-bold text-white">{{ t('admin.cockpit.finance.title') }}</h2>

      <div class="flex items-center gap-3">
        <!-- Currency selector: only shown when >1 currency exists -->
        <select
          v-if="showCurrencySelector"
          v-model="selectedCurrencyIndex"
          class="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-emerald-500"
          :aria-label="t('admin.cockpit.finance.currency_selector')"
        >
          <option
            v-for="(series, idx) in data?.currencies"
            :key="series.currency"
            :value="idx"
          >
            {{ series.currency.toUpperCase() }}
          </option>
        </select>

        <!-- Granularity toggle: Daily / Weekly / Monthly / Quarterly -->
        <div class="flex items-center bg-slate-700 border border-slate-600 rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              bucket === 'daily'
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:text-white'
            "
            @click="emit('update:bucket', 'daily')"
          >
            {{ t('admin.cockpit.finance.daily') }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              bucket === 'weekly'
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:text-white'
            "
            @click="emit('update:bucket', 'weekly')"
          >
            {{ t('admin.cockpit.finance.weekly') }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              bucket === 'monthly'
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:text-white'
            "
            @click="emit('update:bucket', 'monthly')"
          >
            {{ t('admin.cockpit.finance.monthly') }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              bucket === 'quarterly'
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:text-white'
            "
            @click="emit('update:bucket', 'quarterly')"
          >
            {{ t('admin.cockpit.finance.quarterly') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y-3">
      <div class="flex items-end gap-2 h-48">
        <div
          v-for="i in 6"
          :key="i"
          class="flex-1 bg-slate-700 rounded animate-pulse"
          :style="{ height: (30 + i * 10) + '%' }"
        />
      </div>
      <div class="h-3 bg-slate-700 rounded w-1/2 animate-pulse" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-center justify-between"
    >
      <p class="text-red-300 text-sm">{{ t(error) }}</p>
      <button
        type="button"
        class="text-sm text-red-300 hover:text-red-200 font-medium underline"
        @click="emit('retry')"
      >
        {{ t('admin.common.retry') }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!hasData"
      class="flex flex-col items-center justify-center py-16 text-slate-500"
    >
      <p class="text-sm">{{ t('admin.cockpit.finance.empty') }}</p>
    </div>

    <!-- Chart -->
    <div v-else>
      <Suspense>
        <StackedFeeBarChart :buckets="buckets" :currency="currency" />
        <template #fallback>
          <div class="h-48 bg-slate-700 rounded animate-pulse" />
        </template>
      </Suspense>

      <!-- Summary line -->
      <div v-if="summaryLine" class="mt-4 flex flex-wrap gap-4 text-sm">
        <span class="text-slate-400">
          {{ t('admin.cockpit.finance.summary_gross_take') }}
          <span class="text-white font-medium">{{ summaryLine.grossTake }}</span>
        </span>
        <span class="text-slate-400">
          {{ t('admin.cockpit.finance.summary_stripe') }}
          <span class="text-slate-300 font-medium">{{ summaryLine.stripeCost }}</span>
        </span>
        <span class="text-slate-400">
          {{ t('admin.cockpit.finance.summary_net') }}
          <span class="text-emerald-400 font-bold">{{ summaryLine.net }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
